import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useCms } from '../../contexts/CmsContext';
import Button from '../ui/Button';
import {
  Compass,
  BookOpen,
  User,
  LogOut,
  Menu,
  X,
  Sparkles,
  LogIn,
  Bell,
  FileText,
  Info,
  Phone,
  Heart,
  StickyNote,
  MessageSquare,
  GraduationCap,
  BrainCircuit,
  Calendar,
  Gift,
  LayoutDashboard,
  ChevronDown,
  Newspaper,
} from 'lucide-react';

export default function Navbar(): React.JSX.Element {
  const { isAuthenticated, user, clearAuth } = useAuth();
  const { isAdmin, isModerator, isTrainer } = usePermissions();
  const cms = useCms();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    clearAuth();
    navigate('/');
  };

  // Close user menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    if (userMenuOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [userMenuOpen]);

  // Primary nav items (always visible in top bar)
  const primaryNav = isAuthenticated
    ? [
        { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/courses', label: 'Courses', icon: BookOpen },
        { path: '/workshops', label: 'Workshops', icon: Calendar },
        { path: '/news', label: 'AI News', icon: Newspaper },
      ]
    : [
        { path: '/', label: 'Explore', icon: Compass },
        { path: '/courses', label: 'Courses', icon: BookOpen },
        { path: '/workshops', label: 'Workshops', icon: Calendar },
        { path: '/news', label: 'AI News', icon: Newspaper },
      ];

  // User dropdown items (secondary nav)
  const userMenuItems = [
    { path: '/my-courses', label: 'My Courses', icon: BookOpen },
    { path: '/mentor', label: 'Mentor', icon: BrainCircuit },
    { path: '/wishlist', label: 'Wishlist', icon: Heart },
    { path: '/notes', label: 'My Notes', icon: StickyNote },
    { path: '/messages', label: 'Messages', icon: MessageSquare },
    { path: '/referral', label: 'Refer & Earn', icon: Gift },
  ];

  // Role-specific links
  const roleLinks: { path: string; label: string; icon: React.ElementType }[] = [];
  if (isAdmin || isModerator) {
    roleLinks.push({ path: '/admin', label: 'Admin Portal', icon: Sparkles });
  }
  if (isTrainer) {
    roleLinks.push({ path: '/trainer', label: 'Trainer Portal', icon: GraduationCap });
  }
  if (!isTrainer && !isAdmin) {
    roleLinks.push({ path: '/trainer/apply', label: 'Teach With Us', icon: GraduationCap });
  }

  return (
    <nav className="sticky top-0 z-40 glass border-b border-slate-200/60">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0">
            <img
              src="/ecoleminds.png"
              alt="EcoleMinds"
              className="h-8 w-auto object-contain"
            />
          </Link>

          {/* Desktop Primary Nav — centered, minimal */}
          <div className="hidden md:flex items-center gap-1">
            {primaryNav.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${
                    isActive
                      ? 'text-amber-600'
                      : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                  }`}>
                  <Icon className="w-4 h-4" />
                  {item.label}
                  {isActive && (
                    <motion.div
                      layoutId="navbar-indicator"
                      className="absolute inset-0 bg-amber-50 rounded-xl -z-10"
                      transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-2">
            {/* Static pages dropdown */}
            <div className="relative group">
              <button className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-slate-500 hover:text-slate-900 rounded-xl hover:bg-slate-100 transition-colors">
                <Info className="w-4 h-4" />
                More
              </button>
              <div className="absolute top-full right-0 mt-1 w-48 bg-white rounded-xl border border-slate-100 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all py-1 z-50">
                <Link to="/about" className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:text-amber-600 hover:bg-amber-50 transition-colors">
                  <Info className="w-4 h-4" /> About Us
                </Link>
                <Link to="/contact" className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:text-amber-600 hover:bg-amber-50 transition-colors">
                  <Phone className="w-4 h-4" /> Contact
                </Link>
                <Link to="/blog" className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:text-amber-600 hover:bg-amber-50 transition-colors">
                  <FileText className="w-4 h-4" /> Blog
                </Link>
                <Link to="/news" className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:text-amber-600 hover:bg-amber-50 transition-colors">
                  <Newspaper className="w-4 h-4" /> AI News
                </Link>
                <div className="border-t border-slate-100 my-1" />
                <Link to="/terms" className="flex items-center gap-2 px-4 py-2 text-sm text-slate-500 hover:text-amber-600 hover:bg-amber-50 transition-colors">
                  Terms
                </Link>
                <Link to="/privacy" className="flex items-center gap-2 px-4 py-2 text-sm text-slate-500 hover:text-amber-600 hover:bg-amber-50 transition-colors">
                  Privacy
                </Link>
                <Link to="/refund-policy" className="flex items-center gap-2 px-4 py-2 text-sm text-slate-500 hover:text-amber-600 hover:bg-amber-50 transition-colors">
                  Refund Policy
                </Link>
              </div>
            </div>

            {isAuthenticated ? (
              <>
                {/* Notifications */}
                <Link
                  to="/notifications"
                  className="relative p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
                  aria-label="Notifications"
                >
                  <Bell className="w-5 h-5" />
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                </Link>

                {/* User Avatar Dropdown */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-xl hover:bg-slate-100 transition-colors"
                    aria-expanded={userMenuOpen}
                    aria-haspopup="true"
                  >
                    {user?.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.name}
                        className="w-8 h-8 rounded-full object-cover ring-2 ring-slate-100"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-white text-sm font-bold">
                        {user?.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    )}
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {userMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -4, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -4, scale: 0.98 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full right-0 mt-2 w-56 bg-white rounded-2xl border border-slate-100 shadow-xl py-2 z-50"
                      >
                        {/* User name header */}
                        <div className="px-4 py-3 border-b border-slate-50 mb-1">
                          <p className="text-sm font-bold text-slate-900 truncate">{user?.name}</p>
                          <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                        </div>

                        {userMenuItems.map((item) => {
                          const Icon = item.icon;
                          const isActive = location.pathname === item.path;
                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              onClick={() => setUserMenuOpen(false)}
                              className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                                isActive
                                  ? 'text-amber-600 bg-amber-50 font-semibold'
                                  : 'text-slate-600 hover:text-amber-600 hover:bg-amber-50'
                              }`}
                            >
                              <Icon className="w-4 h-4" />
                              {item.label}
                            </Link>
                          );
                        })}

                        {roleLinks.length > 0 && (
                          <>
                            <div className="border-t border-slate-100 my-1" />
                            {roleLinks.map((item) => {
                              const Icon = item.icon;
                              const isActive = location.pathname === item.path;
                              return (
                                <Link
                                  key={item.path}
                                  to={item.path}
                                  onClick={() => setUserMenuOpen(false)}
                                  className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                                    isActive
                                      ? 'text-amber-600 bg-amber-50 font-semibold'
                                      : 'text-slate-600 hover:text-amber-600 hover:bg-amber-50'
                                  }`}
                                >
                                  <Icon className="w-4 h-4" />
                                  {item.label}
                                </Link>
                              );
                            })}
                          </>
                        )}

                        <div className="border-t border-slate-100 my-1" />
                        <Link
                          to="/profile"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                        >
                          <User className="w-4 h-4" />
                          Profile
                        </Link>
                        <button
                          onClick={() => {
                            setUserMenuOpen(false);
                            handleLogout();
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign Out
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            ) : (
              <Link to="/login">
                <Button size="sm" leftIcon={<LogIn className="w-4 h-4" />}>
                  Sign In
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-slate-100 overflow-hidden bg-white/95 backdrop-blur-lg"
          >
            <div className="px-4 py-3 space-y-1">
              {primaryNav.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${
                      isActive
                        ? 'bg-amber-50 text-amber-600'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}>
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}

              {isAuthenticated && (
                <>
                  <div className="border-t border-slate-100 my-2" />
                  <p className="px-4 py-1 text-xs font-bold uppercase tracking-wider text-slate-400">My Account</p>
                  {userMenuItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${
                          isActive
                            ? 'bg-amber-50 text-amber-600'
                            : 'text-slate-600 hover:bg-slate-50'
                        }`}>
                        <Icon className="w-4 h-4" />
                        {item.label}
                      </Link>
                    );
                  })}
                  {roleLinks.map((item) => {
                    const isActive = location.pathname === item.path;
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${
                          isActive
                            ? 'bg-amber-50 text-amber-600'
                            : 'text-slate-600 hover:bg-slate-50'
                        }`}>
                        <Icon className="w-4 h-4" />
                        {item.label}
                      </Link>
                    );
                  })}
                  <Link
                    to="/profile"
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${
                      location.pathname === '/profile'
                        ? 'bg-amber-50 text-amber-600'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <User className="w-4 h-4" /> Profile
                  </Link>
                  <Link
                    to="/notifications"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50"
                  >
                    <Bell className="w-4 h-4" /> Notifications
                  </Link>
                  <button
                    onClick={() => { handleLogout(); setMobileOpen(false); }}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 w-full transition-colors"
                  >
                    <LogOut className="w-4 h-4" /> Sign Out
                  </button>
                </>
              )}

              <div className="border-t border-slate-100 my-2" />
              <p className="px-4 py-1 text-xs font-bold uppercase tracking-wider text-slate-400">Explore</p>
              <Link to="/about" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50">
                <Info className="w-4 h-4" /> About Us
              </Link>
              <Link to="/contact" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50">
                <Phone className="w-4 h-4" /> Contact
              </Link>
              <Link to="/blog" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50">
                <FileText className="w-4 h-4" /> Blog
              </Link>
              <Link to="/news" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50">
                <Newspaper className="w-4 h-4" /> AI News
              </Link>
              <Link to="/terms" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50">
                <FileText className="w-4 h-4" /> Terms
              </Link>
              <Link to="/privacy" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50">
                <FileText className="w-4 h-4" /> Privacy
              </Link>
              <Link to="/refund-policy" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50">
                <FileText className="w-4 h-4" /> Refund Policy
              </Link>

              {!isAuthenticated && (
                <Link to="/login" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-amber-600 hover:bg-amber-50 transition-colors">
                  <LogIn className="w-4 h-4" /> Sign In
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
